import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, CursorTooltip } from '@/ui/primitives';
import { elementOrDescendantHasOverflow } from './overflowTooltipUtils';
import { tableCellTagChipSx } from './sharedCellContent';
import type { ChipProps } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type TableTagChipWithTooltipProps = {
  label: string;
  tone: ChipProps['tone'];
  sx?: SxProps<Theme>;
  tooltipSx?: SxProps<Theme>;
};

export const TableTagChipWithTooltip = ({
  label,
  tone,
  sx = tableCellTagChipSx,
  tooltipSx = tableCellTagChipSx,
}: TableTagChipWithTooltipProps): ReactElement => {
  const chipWrapperRef = useRef<HTMLSpanElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const hasTruncatedLabel = useCallback((): boolean => {
    const chipWrapper = chipWrapperRef.current;
    if (!chipWrapper) return false;
    const chipLabel = chipWrapper.querySelector<HTMLElement>('.MuiChip-label');
    if (!chipLabel) return false;
    return elementOrDescendantHasOverflow(chipLabel);
  }, []);

  const handleOpen = useCallback(() => {
    setTooltipOpen(hasTruncatedLabel());
  }, [hasTruncatedLabel]);

  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  useEffect(() => {
    if (!tooltipOpen || hasTruncatedLabel()) return;
    setTooltipOpen(false);
  }, [hasTruncatedLabel, tooltipOpen]);

  return (
    <CursorTooltip
      open={tooltipOpen}
      title={<Chip label={label} size="xs" tone={tone} sx={tooltipSx} />}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            px: '8px',
            py: '6px',
            width: 'fit-content',
          },
        },
      }}
    >
      <Box
        ref={chipWrapperRef}
        component="span"
        onBlur={handleClose}
        onFocus={handleOpen}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{ display: 'inline-flex', maxWidth: '100%', minWidth: 0 }}
      >
        <Chip label={label} size="xs" tone={tone} sx={sx} />
      </Box>
    </CursorTooltip>
  );
};
